
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SaveIcon, BanIcon, PlusIcon, TrashIcon, Loader2Icon, EyeIcon, DownloadIcon, ShieldIcon } from '@/components/Icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DocumentPreview } from '@/components/ui/DocumentPreview';
import { generatePDF } from '@/utils/pdfGenerator';
import { VerifactuCard } from '@/components/VerifactuCard';
import { toast } from 'sonner';
import { Cliente, Factura, Rectificativa, InvoiceItem, Tax, PaymentMethod } from '@shared/types';
import { calcularImportes, centimosAEuros, importeFinitoOCero } from '@shared/importes';
import { AVISO_IMPORTES_INVALIDOS, calcularImportesDocumento } from '@/utils/importesDocumento';
import { Alert, AlertDescription } from '@/components/ui/alert';

const createNewRectificativa = (): Partial<Rectificativa> => ({
    numero: '',
    clienteId: '',
    facturaOriginalId: '',
    facturaOriginalNumero: '',
    fecha: new Date().toISOString().slice(0, 10),
    motivo: '',
    items: [],
    total: 0,
    estado: 'Borrador',
});

export default function RectificativaForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchWithAuth, userProfile } = useUser();
    const [rectificativa, setRectificativa] = useState<Partial<Rectificativa>>(createNewRectificativa());
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [facturas, setFacturas] = useState<Factura[]>([]);
    const [impuestos, setImpuestos] = useState<Tax[]>([]);
    const [metodosPago, setMetodosPago] = useState<PaymentMethod[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Detectar parámetro showPreview en la URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('showPreview') === 'true') {
            setShowPreview(true);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [clientesRes, facturasRes, impuestosRes, metodosRes, nextNumRes] = await Promise.all([
                    fetchWithAuth('/api/clientes'),
                    fetchWithAuth('/api/facturas'),
                    fetchWithAuth('/api/impuestos'),
                    fetchWithAuth('/api/metodos-pago'),
                    fetchWithAuth('/api/rectificativas/next-number')
                ]);
                if (!clientesRes.ok || !facturasRes.ok || !impuestosRes.ok || !metodosRes.ok) {
                    throw new Error('No se pudieron cargar los datos necesarios.');
                }
                const [clientesData, facturasData, impuestosData, metodosData] = await Promise.all([
                    clientesRes.json(),
                    facturasRes.json(),
                    impuestosRes.json(),
                    metodosRes.json()
                ]);
                setClientes(clientesData);
                setFacturas(facturasData);
                setImpuestos(impuestosData);
                setMetodosPago(metodosData);

                if (id) {
                    const res = await fetchWithAuth(`/api/rectificativas/${id}`);
                    if (!res.ok) throw new Error('Factura rectificativa no encontrada');
                    setRectificativa(await res.json());
                } else {
                    const { nextNumber } = await nextNumRes.json();
                    setRectificativa({...createNewRectificativa(), numero: nextNumber});
                }
            } catch (error) {
                toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
                navigate('/rectificativas');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id, navigate, fetchWithAuth]);

    // Cálculo en céntimos enteros (FISC-10), mismo criterio de resolución de
    // tipo que antes (impuestosMap.get(...) || 0). Este formulario no expone
    // "Aplicar IRPF" (no hay campo aplicarIrpf aquí) y handleSelectFactura no
    // copia el aplicarIrpf de la factura original, así que el servidor
    // tampoco descuenta IRPF para una rectificativa creada por este
    // formulario (irpfRate = 0 en routes/rectificativas.ts): a diferencia de
    // FacturaForm, aquí no hay discrepancia de IRPF entre lo que se muestra
    // y lo que se guarda.
    //
    // Hallazgo de revisión R3-002: cantidad/precioUnitario se sanean con
    // importeFinitoOCero ANTES de calcularImportes, porque este useMemo se
    // ejecuta durante el render — una factura original cargada con un
    // importe no finito lanzaría aquí y desmontaría el formulario entero.
    // Lo que se envía al guardar sigue llevando los valores originales del
    // item (ver handleSave: ...rectificativa); el servidor valida y
    // recalcula, y es él quien debe seguir fallando fuerte.
    //
    // En una rectificativa emitida con instantánea (`importesEmision`) el resumen
    // muestra el total guardado al emitir, no el calculado con los impuestos de hoy;
    // si la instantánea está mal formada se avisa y se muestra el recalculado.
    const importesDoc = useMemo(() => {
        if (!rectificativa.items) return null;
        const impuestosMap = new Map(impuestos.map((i: Tax) => [i.id, i.rate]));
        const lineas = rectificativa.items.map((item) => ({
            cantidad: importeFinitoOCero(item.cantidad),
            precioUnitario: importeFinitoOCero(item.precioUnitario),
            rate: impuestosMap.get(item.taxId) || 0,
        }));
        return calcularImportesDocumento(rectificativa, lineas, 0);
    }, [rectificativa, impuestos]);
    const total = importesDoc?.total ?? 0;

    const handleFieldChange = (field: keyof Rectificativa, value: any) => {
        setRectificativa(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (itemId: string, field: keyof InvoiceItem, value: any) => {
        const items = rectificativa.items?.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
        ) || [];
        setRectificativa(prev => ({ ...prev, items }));
    };

    const handleSelectFactura = (facturaId: string) => {
        const factura = facturas.find(f => f.id === facturaId);
        if (!factura) return;
        setRectificativa(prev => ({
            ...prev,
            facturaOriginalId: factura.id,
            facturaOriginalNumero: factura.numero,
            clienteId: factura.clienteId,
            // Copia los items con valores negativos
            items: factura.items.map(item => ({
                ...item,
                id: uuidv4(),
                cantidad: -Math.abs(item.cantidad),
                precioUnitario: item.precioUnitario
            }))
        }));
    };

    const addItem = () => {
        const defaultTax = impuestos.find((i: Tax) => i.isDefault)?.id || impuestos[0]?.id || '';
        const newItem: InvoiceItem = {
            id: uuidv4(),
            concepto: '',
            descripcion: '',
            cantidad: -1,
            precioUnitario: 0,
            taxId: defaultTax
        };
        handleFieldChange('items', [...(rectificativa.items || []), newItem]);
    };

    const removeItem = (itemId: string) => {
        handleFieldChange('items', rectificativa.items?.filter(item => item.id !== itemId));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const dataToSave = { ...rectificativa, total };
            const response = await fetchWithAuth(id ? `/api/rectificativas/${id}` : '/api/rectificativas', {
                method: id ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSave),
            });
            if (!response.ok) throw new Error(await response.text());
            toast.success(`Factura rectificativa ${id ? 'actualizada' : 'creada'}`);
            navigate('/rectificativas');
        } catch (error) {
            toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Error desconocido' });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>;

    const selectedCliente = clientes.find(c => c.id === rectificativa.clienteId);
    const selectedPaymentMethod = metodosPago.find(m => m.id === rectificativa.paymentMethodId);

    // 🔒 VERIFACTU PROTECTION: Emitted rectificativas with Verifactu are IMMUTABLE
    const isVerifactuLocked = rectificativa.estado !== 'Borrador' && !!rectificativa.verifactu;

    return (
        <>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold tracking-tighter">{id ? 'Editar' : 'Nueva'} Factura Rectificativa</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/rectificativas')} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
                    <Button variant="outline" onClick={() => setShowPreview(true)} disabled={!rectificativa.clienteId}><EyeIcon className="mr-2" />Vista Previa</Button>
                    {!isVerifactuLocked && <Button onClick={handleSave} disabled={isSaving}><SaveIcon className="mr-2" />{isSaving ? 'Guardando...' : 'Guardar'}</Button>}
                </div>
            </div>

            {isVerifactuLocked && (
                <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <ShieldIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-yellow-900">Factura Rectificativa Protegida por VeriFactu</h3>
                                <p className="text-sm text-yellow-800 mt-1">
                                    Esta factura rectificativa ha sido registrada con datos de integridad VeriFactu (Nº Registro: {rectificativa.verifactu?.numeroRegistro}) y forma parte de la cadena criptográfica.
                                    <br />
                                    <strong>Los datos no pueden ser modificados</strong> según la normativa fiscal española.
                                    Si hay un error, debes anularla mediante otra factura rectificativa.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Información Principal</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label>Factura a rectificar</Label>
                                <Select onValueChange={handleSelectFactura} value={rectificativa.facturaOriginalId} disabled={!!id || isVerifactuLocked}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar factura..." /></SelectTrigger>
                                    <SelectContent>{facturas.map(f => <SelectItem key={f.id || ''} value={f.id || ''}>{`${f.numero} (${clientes.find(c => c.id === f.clienteId)?.nombre || ''})`}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Cliente</Label>
                                <Input value={clientes.find(c => c.id === rectificativa.clienteId)?.nombre || 'Cliente no seleccionado'} readOnly disabled />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="numero">Número de Rectificativa</Label>
                                <Input id="numero" value={rectificativa.numero || ''} onChange={e => handleFieldChange('numero', e.target.value)} disabled={isVerifactuLocked} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="fecha">Fecha</Label>
                                <Input id="fecha" type="date" value={rectificativa.fecha || ''} onChange={e => handleFieldChange('fecha', e.target.value)} disabled={isVerifactuLocked} />
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <Label htmlFor="motivo">Motivo de la rectificación</Label>
                                <Textarea id="motivo" value={rectificativa.motivo || ''} onChange={e => handleFieldChange('motivo', e.target.value)} placeholder="Ej: Devolución de producto, error en factura..." disabled={isVerifactuLocked} />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Líneas de la Factura</CardTitle>
                            <CardDescription>
                                Las líneas se copian automáticamente de la factura original con importes en negativo.
                                <strong className="text-yellow-700"> No pueden ser modificadas.</strong>
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Concepto</TableHead>
                                        <TableHead className="w-24">Cant.</TableHead>
                                        <TableHead className="w-32">Precio Unit.</TableHead>
                                        <TableHead className="w-32">Impuesto</TableHead>
                                        <TableHead className="w-32">Total</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(rectificativa.items?.length ?? 0) > 0 ? (
                                        rectificativa.items!.map(item => {
                                            const tax = impuestos.find((i: Tax) => i.id === item.taxId);
                                            // R3-002: mismo saneado que el "total" de arriba — este cálculo
                                            // también corre durante el render de cada fila.
                                            const lineaCalculada = calcularImportes([
                                                {
                                                    cantidad: importeFinitoOCero(item.cantidad),
                                                    precioUnitario: importeFinitoOCero(item.precioUnitario),
                                                    rate: tax?.rate || 0,
                                                },
                                            ]).lineas[0];
                                            // R3-005: la cuota de esta fila es informativa (se redondea por su
                                            // lado); la autoritativa es la cuota agregada por tipo de IVA del
                                            // resumen ("Total a abonar"), así que pueden diferir en un céntimo.
                                            const itemTotal = centimosAEuros(lineaCalculada.baseCentimos + lineaCalculada.cuotaCentimos);
                                            return (
                                                <TableRow key={item.id} className="bg-muted/30">
                                                    <TableCell className="font-medium">{item.concepto}</TableCell>
                                                    <TableCell>{item.cantidad.toFixed(2)}</TableCell>
                                                    <TableCell>{item.precioUnitario.toFixed(2)} €</TableCell>
                                                    <TableCell>{tax?.nombre} ({(tax ? tax.rate * 100 : 0).toFixed(0)}%)</TableCell>
                                                    <TableCell className="font-bold">{itemTotal.toFixed(2)} €</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                Selecciona una factura para cargar sus líneas
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:sticky lg:top-24 space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Resumen</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            {importesDoc?.fuente === 'instantanea-invalida' && (
                                <Alert variant="destructive"><AlertDescription>{AVISO_IMPORTES_INVALIDOS}</AlertDescription></Alert>
                            )}
                            <div className="flex justify-between font-bold text-xl">
                                <span>Total a abonar</span>
                                <span>{total.toFixed(2)} €</span>
                            </div>
                             <div className="space-y-2 pt-4">
                                <Label>Estado</Label>
                                <Select value={rectificativa.estado} onValueChange={value => handleFieldChange('estado', value)} disabled={isVerifactuLocked}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Borrador">Borrador</SelectItem>
                                        <SelectItem value="Emitida">Emitida</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {rectificativa.verifactu && (
                        <VerifactuCard verifactu={rectificativa.verifactu} facturaNumero={rectificativa.numero || ''} estado={rectificativa.estado || 'Borrador'} />
                    )}

                     <Card>
                        <CardHeader><CardTitle>Notas Adicionales</CardTitle></CardHeader>
                        <CardContent>
                            <Textarea placeholder="Añade notas internas si es necesario..." value={rectificativa.notas || ''} onChange={e => handleFieldChange('notas', e.target.value)} disabled={isVerifactuLocked} />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>

        {/* Diálogo de Vista Previa */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex justify-between items-center">
                        <DialogTitle>Vista Previa - Factura Rectificativa {rectificativa.numero}</DialogTitle>
                        <Button variant="outline" size="sm" onClick={() => generatePDF('document-preview-pdf', `rectificativa-${rectificativa.numero}.pdf`)}>
                            <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
                        </Button>
                    </div>
                    <DialogDescription className="sr-only">
                      Vista previa del documento de factura rectificativa antes de imprimir o descargar en PDF
                    </DialogDescription>
                </DialogHeader>
                {selectedCliente && userProfile && rectificativa.items && (
                    <DocumentPreview
                        document={{
                            ...rectificativa,
                            tipo: 'FACTURA_RECTIFICATIVA',
                            items: rectificativa.items || []
                        } as any}
                        client={selectedCliente}
                        freelance={userProfile}
                        taxes={impuestos}
                        paymentMethod={selectedPaymentMethod}
                    />
                )}
                <div className="flex justify-center pt-4 border-t">
                    <Button variant="outline" onClick={() => generatePDF('document-preview-pdf', `rectificativa-${rectificativa.numero}.pdf`)}>
                        <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
}
